import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  PostVideoCommentRepliesResult,
  PostVideoCommentRepliesOptions,
  GetVideoCommentRepliesOptions,
  GetVideoCommentRepliesResult,
  getVideoCommentReplies,
  PostVideoCommentRepliesFields,
  postVideoCommentReplies,
  GetVideoCommentRepliesFields,
} from '../fetchers/video/comment/replies';
import { useCallback } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import useSWRInfinite, { SWRInfiniteConfiguration } from 'swr/infinite';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetVideoCommentRepliesOptions<Fields extends GetVideoCommentRepliesFields> =
  GetVideoCommentRepliesOptions<Fields>;

/**
 * Get all the replies to a video comment
 * This method returns every reply to the specified video comment.
 */

export function useGetVideoCommentReplies<Fields extends GetVideoCommentRepliesFields>(
  getOptions:
    | UseGetVideoCommentRepliesOptions<Fields>
    | (() => UseGetVideoCommentRepliesOptions<Fields> | null),
  config?: SWRConfiguration<GetVideoCommentRepliesResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options
      ? `/videos/${options.where.videoId}/comments/${
          options.where.commentId
        }/replies${serializeQuery(options)}`
      : () => null,
    options
      ? () => {
          return getVideoCommentReplies({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoCommentReplies, {
    endpoint: '/videos/:videoId/comments/:commentId/replies',
    method: 'GET',
  });
}

/**
 * Get all the replies to a video comment
 * This method returns every reply to the specified video comment.
 */

export function useGetVideoCommentRepliesLazy<Fields extends GetVideoCommentRepliesFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetVideoCommentRepliesResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetVideoCommentRepliesOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}/comments/${
            options.where.commentId
          }/replies${serializeQuery(options)}`,
          getVideoCommentReplies({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoCommentRepliesLazy, {
    endpoint: '/videos/:videoId/comments/:commentId/replies',
    method: 'GET',
  });
}

type UseGetVideoCommentRepliesOptionsInfinite<Fields extends GetVideoCommentRepliesFields> =
  GetVideoCommentRepliesOptions<Fields>;

/**
 * Get all the replies to a video comment
 * This method returns every reply to the specified video comment.
 */

export function useGetVideoCommentRepliesInfinite<Fields extends GetVideoCommentRepliesFields>(
  getOptions:
    | UseGetVideoCommentRepliesOptionsInfinite<Fields>
    | (() => UseGetVideoCommentRepliesOptionsInfinite<Fields> | null),
  config?: SWRInfiniteConfiguration<GetVideoCommentRepliesResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let getKey = (
    pageIndex: number,
    previousPageData: GetVideoCommentRepliesResult<Fields> | null,
  ) => {
    if (options === null) {
      return null;
    }
    if (previousPageData && !previousPageData.paging.next) {
      return null;
    }
    let { perPage = 25, page: _page, ...rest } = options.query ?? {};
    let select = options.select.join(',');
    let queryString = Object.entries(rest ?? {})
      .filter(([, value]) => typeof value !== 'undefined')
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    return [
      `/videos/${options.where.videoId}/comments/${options.where.commentId}/replies?page=${
        pageIndex + 1
      }&perPage=${perPage}&fields=${select}&${queryString}`,
      pageIndex,
    ];
  };
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWRInfinite(
    getKey,
    options !== null
      ? ([_uri, pageIndex]: [string, number]) => {
          return getVideoCommentReplies({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            query: { ...options.query, page: pageIndex + 1 },
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoCommentRepliesInfinite, {
    endpoint: '/videos/:videoId/comments/:commentId/replies',
    method: 'GET',
  });
}

type UsePostVideoCommentRepliesOptions<Fields extends PostVideoCommentRepliesFields> =
  PostVideoCommentRepliesOptions<Fields>;

/**
 * Add a reply to a video comment
 * This method adds a reply to the specified video comment.
 */

export function usePostVideoCommentReplies<Fields extends PostVideoCommentRepliesFields>() {
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PostVideoCommentRepliesResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePostVideoCommentRepliesOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await postVideoCommentReplies({
          ...options,
          baseUrl,
          headers: {
            ...options.headers,
            'Content-Type': 'application/json',
            Authorization: jwt ? `jwt ${jwt}` : '',
            'Vimeo-Page': `${xVimeoPage}`,
            'Accept-Language': locale ?? 'en',
          },
        });
        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePostVideoCommentReplies, {
    endpoint: '/videos/:videoId/comments/:commentId/replies',
    method: 'POST',
  });
}
